'use client'
import React, { useState } from 'react'
import Link from 'next/link'
import { toast } from 'react-toastify'
import { splitHeading } from '@/constants/hooks'
import { apiSubscribeNewsletter } from '@/apis/page_optimization'
import { RecaptchaProvider } from '@/components/shared/RecaptchaProvider'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { apiVerifyCaptcha } from '@/apis/captcha';

interface Props {
  companyId: string;
  domain?: string;
}

const NewsletterInner = ({ companyId, domain = '' }: Props) => {
  const [email, setEmail] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const validateEmail = (emailStr: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(emailStr);
  };

  const [first, second] = splitHeading("Never Miss a Winning Deal");

  const handleRate = async (e: React.FormEvent, p_email: string) => {
    e.preventDefault();

    if (loading) return;

    if (!p_email || !p_email.trim()) {
      toast.error('Email field is required.', { autoClose: 2000 });
      return;
    }

    if (!validateEmail(p_email)) {
      toast.error("Please enter a valid email address.", { autoClose: 2000 });
      return;
    }

    setEmail(p_email);
    await handleSubmit(p_email);
  };

  const handleSubmit = async (p_email: string) => {
    setLoading(true);

    if (executeRecaptcha) {
      try {
        const recaptchaToken = await executeRecaptcha('newsletter');
        if (recaptchaToken) {
          const captchaRes = await apiVerifyCaptcha(domain, recaptchaToken);
          if (captchaRes && captchaRes.verified === false) {
            toast.error('Captcha verification failed. Please try again.', { autoClose: 2000 });
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.warn('reCAPTCHA execution skipped:', err);
      }
    }

    try {

      const response = await apiSubscribeNewsletter(companyId, p_email);
      const resMsg = typeof response?.message === 'string' ? response.message : '';

      const isSuccess =
        (response?.status === true ||
          response?.status === 200 ||
          resMsg === "Subscribed successfully" ||
          resMsg === "Subscribed successfully!") &&
        !resMsg.includes("Server responded with status");

      if (isSuccess) {
        toast.success("Thank you for your feedback!", { autoClose: 2000 });
        setEmail("");
      } else {
        toast.info("You have already subscribed to our newsletter.", { autoClose: 2500 });
        setEmail("");
      }
    } catch (error: any) {
      console.error("Error subscribing:", error);
      toast.info("You have already subscribed to our newsletter.", { autoClose: 2500 });
      setEmail("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-12 sm:py-16 md:py-24 bg-[#fafafa] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-16">
        <div className="relative bg-slate-950 border border-indigo-500/20 rounded-3xl md:rounded-[2.5rem] p-6 sm:p-8 md:p-12 lg:p-20 overflow-hidden shadow-2xl">

          {/* Mesh Gradient Glows (Matching Header) */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-600/20 rounded-full blur-[120px] -mr-48 -mt-48 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-600/20 rounded-full blur-[100px] -ml-36 -mb-36 pointer-events-none"></div>

          <div className="relative z-10 grid lg:grid-cols-2 gap-8 lg:gap-20 items-center">

            {/* Text Content */}
            <div className="text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/10 text-indigo-300 text-[10px] sm:text-[11px] font-black uppercase tracking-widest mb-4 sm:mb-6 backdrop-blur-md">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                Stay Updated
              </div>
              <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white leading-[1.15] mb-4 sm:mb-6 tracking-tight">
                {first} <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400 inline-block pb-[0.35em] -mb-[0.35em]" style={{ backgroundSize: '200% auto', animation: 'heroShimmer 6s linear infinite', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{second}</span>
              </h2>
              <p className="text-sm sm:text-md text-slate-200 mb-0 max-w-md leading-relaxed font-medium">
                Get a curated list of the world&apos;s best offers and exclusive coupon codes, delivered weekly. No spam, just pure savings.
              </p>
            </div>

            {/* Form Area */}
            <div className="bg-white/[0.03] backdrop-blur-xl p-5 sm:p-8 md:p-10 rounded-2xl md:rounded-[2rem] border border-white/10 shadow-[0_8px_30px_rgb(0,0,0,0.12)]">
              <form onSubmit={(e) => handleRate(e, email)} className="flex flex-col gap-4 sm:gap-5">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 sm:pl-5 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" /></svg>
                  </div>
                  <input
                    type="email"
                    name="email"
                    aria-label="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address..."
                    disabled={loading}
                    className="w-full bg-slate-900/50 border border-white/10 text-white pl-10 sm:pl-12 pr-4 sm:pr-6 py-3 sm:py-4 text-xs sm:text-base rounded-xl focus:border-indigo-500 focus:outline-none outline-none placeholder:text-slate-500 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <button type="submit" disabled={loading} className="w-full group relative flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-600 hover:from-indigo-500 hover:to-indigo-600 text-white px-5 sm:px-8 py-3.5 sm:py-4 text-xs sm:text-sm rounded-xl transition-all duration-300 font-black uppercase tracking-widest shadow-lg hover:shadow-indigo-500/25 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? "Subscribing..." : "Subscribe Now"}
                  {!loading && (
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                  )}
                </button>
              </form>
            </div>

          </div>
        </div>
      </div>
    </section>
  )
}

const Newsletter = (props: Props) => {
  return (
    <RecaptchaProvider domain={props.domain || ''}>
      <NewsletterInner {...props} />
    </RecaptchaProvider>
  )
}

export default Newsletter